import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Alert,
  Typography,
  Box,
  CircularProgress
} from '@mui/material';
import { Schema } from '../../services/schemaApiService';
import axios from 'axios';

interface RedoSchemaDialogProps {
  open: boolean;
  onClose: () => void;
  schema: Schema | null;
  onSuccess: () => void;
}

const contentTypes = [
  {
    value: 'paid-attraction',
    label: 'Paid Tourist Attraction',
    description: 'Museums, observatories, tours with admission fees'
  },
  {
    value: 'free-attraction',
    label: 'Free Tourist Attraction',
    description: 'Parks, bridges, public spaces'
  },
  {
    value: 'holiday-event',
    label: 'Holiday/Event',
    description: 'Christmas, Labor Day, annual celebrations'
  },
  {
    value: 'landmark',
    label: 'Landmark',
    description: 'Historic buildings, monuments, notable locations'
  },
  {
    value: 'hotel',
    label: 'Hotel/Accommodation',
    description: 'Hotels, hostels, lodging'
  },
  {
    value: 'restaurant',
    label: 'Restaurant/Dining',
    description: 'Restaurants, cafes, food establishments'
  },
  {
    value: 'shopping-center',
    label: 'Shopping Center',
    description: 'Malls, outlet stores, shopping centers'
  },
  {
    value: 'museum',
    label: 'Museum',
    description: 'Art, history, science museums (uses Museum + TouristAttraction + LocalBusiness)'
  },
  {
    value: 'park',
    label: 'Park',
    description: 'Public parks, green spaces (uses Park + TouristAttraction)'
  },
  {
    value: 'tourist-destination',
    label: 'Tourist Destination',
    description: 'Neighborhoods, boroughs, area guides'
  },
  {
    value: 'tourist-trip',
    label: 'Tourist Trip/Itinerary',
    description: 'Walking tours, multi-stop experiences, itineraries'
  },
  {
    value: 'entertainment',
    label: 'Entertainment Venue',
    description: 'Theaters, concert halls, entertainment'
  },
  {
    value: 'theater-venue',
    label: 'Theater Venue (PerformingArtsTheater)',
    description: 'Broadway theaters showing multiple productions'
  }
];

const RedoSchemaDialog: React.FC<RedoSchemaDialogProps> = ({
  open,
  onClose,
  schema,
  onSuccess
}) => {
  const [selectedType, setSelectedType] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleRedo = async () => {
    if (!schema || !selectedType) return;

    setLoading(true);
    setError('');

    try {
      const response = await axios.post('/api/schema-management/redo', {
        url: schema.url,
        newContentType: selectedType,
        websiteId: 'newyork.co.uk',
        isSkipped: schema.is_skipped || false
      });

      if (response.data.success) {
        onSuccess();
        onClose();
      } else {
        setError(response.data.error || 'Failed to regenerate schema');
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  if (!schema) return null;

  // Fix URL display
  let displayUrl = schema.url;
  if (!displayUrl.startsWith('http')) {
    displayUrl = displayUrl.replace(/^www_/, 'https://www.')
                          .replace(/_co_uk_/, '.co.uk/')
                          .replace(/_/g, '-');
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {schema?.is_skipped 
          ? 'Generate Schema for Skipped URL' 
          : 'Regenerate Schema with Different Type'}
      </DialogTitle>
      <DialogContent>
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Current URL:
          </Typography>
          <Typography variant="body2" sx={{ fontWeight: 500, mb: 2 }}>
            {displayUrl}
          </Typography>
          
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Current Type:
          </Typography>
          <Typography variant="body2" sx={{ fontWeight: 500, mb: 3 }}>
            {schema.content_type}
          </Typography>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <FormControl component="fieldset" fullWidth>
          <FormLabel component="legend">Select New Schema Type</FormLabel>
          <RadioGroup
            value={selectedType}
            onChange={(e) => setSelectedType(e.target.value)}
          >
            {contentTypes.map((type) => (
              <FormControlLabel
                key={type.value}
                value={type.value}
                control={<Radio />}
                disabled={type.value === schema.content_type}
                label={
                  <Box>
                    <Typography variant="body1">{type.label}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {type.description}
                    </Typography>
                  </Box>
                }
                sx={{ mb: 1 }}
              />
            ))}
          </RadioGroup>
        </FormControl>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          onClick={handleRedo}
          variant="contained"
          disabled={!selectedType || loading}
          startIcon={loading && <CircularProgress size={16} />}
        >
          {loading ? 'Regenerating...' : 'Regenerate Schema'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default RedoSchemaDialog;